import { useEffect, useRef, type RefObject } from "react";
import clsx from "clsx";
import { faClipboardCheck } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { PanelEmptyState } from "@/components/mission-control/PanelEmptyState";
import type { ScreeningDocument } from "@/services/api/guardiansv2/types";
import { ScreeningCard } from "./ScreeningCard";

export interface ScreeningsPaneProps {
  screenings: ScreeningDocument[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  scrollRootRef: RefObject<HTMLElement | null>;
  listClassName?: string;
}

export function ScreeningsPane({
  screenings,
  loading,
  loadingMore,
  error,
  selectedId,
  onSelect,
  hasMore,
  onLoadMore,
  scrollRootRef,
  listClassName,
}: ScreeningsPaneProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = scrollRootRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { root, rootMargin: "200px", threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, scrollRootRef, screenings.length]);

  if (error) {
    return (
      <p className="text-sm text-red-300" data-testid="screenings-error">
        {error}
      </p>
    );
  }

  if (loading && screenings.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-400" data-testid="screenings-loading">
        Loading…
      </p>
    );
  }

  if (!loading && screenings.length === 0) {
    return (
      <PanelEmptyState
        icon={faClipboardCheck}
        title="No screenings"
        subtitle="Screenings appear when alert screening runs after tasks or missions complete."
        testId="screenings-empty"
      />
    );
  }

  return (
    <div className={clsx("space-y-2 p-1", listClassName)} data-testid="screenings-pane">
      {screenings.map((screening) => (
        <ScreeningCard
          key={screening._id}
          screening={screening}
          selected={selectedId === screening._id}
          onClick={() => onSelect(screening._id)}
        />
      ))}
      <div ref={sentinelRef} className="h-1 w-full shrink-0" aria-hidden />
      {loadingMore ? (
        <p className="py-3 text-center text-sm text-slate-400" data-testid="screenings-loading-more">
          Loading more…
        </p>
      ) : null}
    </div>
  );
}
